import { useCallback, useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  Configuration,
  Port,
  PortApi,
  type PortExtended,
  PortList,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const usePortsApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const portserverApi = useMemo(
    () => new PortApi(configuration),
    [configuration]
  );
  return apiClientWrapper(portserverApi) as PortApi;
};

export const usePortsGetList = (
  pattern = '',
  offset = 0,
  limit = 10000
): UseQueryResult<Record<'records', Array<PortList>>> => {
  const portserverApi = usePortsApi();

  return useQuery(['portListGet', pattern, offset, limit], async () => {
    return await portserverApi.portListGet({ pattern, offset, limit });
  });
};

export const usePortFetch = (): ((
  portId: number | string
) => Promise<PortExtended>) => {
  const portserverApi = usePortsApi();
  const queryClient = useQueryClient();

  const fetchPort = useCallback(
    async (portId: number | string) => {
      try {
        // First check if data exists in cache
        const cachedData = queryClient.getQueryData(['port', portId]);
        if (cachedData) {
          return cachedData;
        }

        // If not in cache, fetch from API
        const data = await portserverApi.portInfoIdGet({ id: portId });

        // Cache the result
        queryClient.setQueryData(['port', portId], data);

        return data;
      } catch (error) {
        console.error('Error fetching port:', error);
        throw error;
      }
    },
    [portserverApi, queryClient]
  );

  return fetchPort;
};

export const usePortGet = (portId: number | string) => {
  const portserverApi = usePortsApi();

  return useQuery(
    ['port', portId],
    async () => {
      const data = await portserverApi.portInfoIdGet({ id: portId });
      return data;
    },
    {
      enabled: !!portId, // Only fetch if portId is defined
    }
  );
};

export const usePortArrivals = (portId?: number) => {
  const portserverApi = usePortsApi();

  return useQuery(
    ['arrivals', portId],
    async () => {
      if (!portId) {
        throw new Error('Port ID is required but was not provided');
      }

      const data = await portserverApi.portArrivalsIdGet({ id: portId });
      return data;
    },
    {
      enabled: !!portId,
      refetchInterval: 30000, // 30 seconds in milliseconds
      refetchIntervalInBackground: true, // Continue polling even when tab is not active
    }
  );
};
